import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Send, X, Sparkles, RefreshCw, Mail, CheckSquare, Square, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

const STATUS_LABELS = {
  pending_review: { label: "Do zatwierdzenia", color: "amber" },
  sent: { label: "Wyslane", color: "green" },
  rejected: { label: "Odrzucone", color: "gray" },
  send_failed: { label: "Blad wysylki", color: "red" },
  edited: { label: "Edytowane", color: "blue" },
};

export default function MailDrafts() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ by_status: {}, last_7d_total: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending_review");
  const [selected, setSelected] = useState(null);
  const [editBody, setEditBody] = useState("");
  const [editSubject, setEditSubject] = useState("");
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [listR, statsR] = await Promise.all([
        axios.get(`${API}/api/mail/pending-auto-replies?status=${statusFilter}&limit=200`, cfg),
        axios.get(`${API}/api/mail/pending-auto-replies-stats`, cfg),
      ]);
      setItems(listR.data.items || []);
      setStats(statsR.data || { by_status: {}, last_7d_total: 0 });
    } catch (e) {
      toast.error(`Blad ladowania: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const openDraft = (d) => {
    setSelected(d);
    setEditBody(d.draft_reply || "");
    setEditSubject(d.reply_subject || `Re: ${d.subject_original || ""}`);
  };

  const closeDraft = () => {
    setSelected(null); setEditBody(""); setEditSubject("");
  };

  const saveEdit = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/api/mail/pending-auto-replies/${selected.id}`,
        { draft_reply: editBody, reply_subject: editSubject },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Zapisane");
      await load();
      setSelected({ ...selected, draft_reply: editBody, reply_subject: editSubject });
    } catch (e) {
      toast.error(`Blad zapisu: ${e?.response?.data?.detail || e.message}`);
    } finally { setSaving(false); }
  };

  const send = async () => {
    if (!selected) return;
    if (!confirm(`Wyslac odpowiedz do ${selected.to}?`)) return;
    setSending(true);
    try {
      // save edit first if changed
      if (editBody !== selected.draft_reply || editSubject !== selected.reply_subject) {
        await axios.patch(`${API}/api/mail/pending-auto-replies/${selected.id}`,
          { draft_reply: editBody, reply_subject: editSubject },
          { headers: { Authorization: `Bearer ${token}` } });
      }
      const r = await axios.post(`${API}/api/mail/pending-auto-replies/${selected.id}/send`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      if (r.data?.error) {
        toast.error(r.data.error);
      } else {
        toast.success("Wyslane!");
        closeDraft();
        await load();
      }
    } catch (e) {
      toast.error(`Blad wysylki: ${e?.response?.data?.detail || e.message}`);
    } finally { setSending(false); }
  };

  const reject = async () => {
    if (!selected) return;
    const reason = prompt("Powod odrzucenia (opcjonalny):") || "";
    setSending(true);
    try {
      await axios.post(`${API}/api/mail/pending-auto-replies/${selected.id}/reject`, { reason },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Odrzucone");
      closeDraft();
      await load();
    } catch (e) {
      toast.error(`Blad: ${e?.response?.data?.detail || e.message}`);
    } finally { setSending(false); }
  };

  const toggleSelect = (id) => {
    const s = new Set(selectedIds);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelectedIds(s);
  };
  const selectAll = () => setSelectedIds(new Set(items.map((i) => i.id)));
  const clearSelection = () => setSelectedIds(new Set());

  const bulkAction = async (action) => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    const label = action === "send" ? "wyslac" : "odrzucic";
    if (!confirm(`Na pewno ${label} ${ids.length} draftow?`)) return;
    let reason = "";
    if (action === "reject") reason = prompt("Powod odrzucenia (opcjonalny):") || "";
    setBulkLoading(true);
    try {
      const r = await axios.post(`${API}/api/mail/pending-auto-replies/bulk`,
        { action, ids, reason },
        { headers: { Authorization: `Bearer ${token}` } });
      if (r.data?.error) {
        toast.error(r.data.error);
      } else {
        toast.success(`${action === "send" ? "Wyslane" : "Odrzucone"}: ${r.data.ok} OK / ${r.data.failed} bledow`);
        clearSelection();
        await load();
      }
    } catch (e) {
      toast.error(`Blad bulk: ${e?.response?.data?.detail || e.message}`);
    } finally { setBulkLoading(false); }
  };

  return (
    <div className="space-y-6 max-w-none">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-amber-500" />
            Drafty AI do wyslania
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Odpowiedzi wygenerowane przez cron Mail Auto-Processor (Sprint 8.A).
            Zatwierdz jednym klikiem lub odrzuc.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
          Odswiez
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {["pending_review", "sent", "rejected", "send_failed", "edited"].map((s) => {
          const info = STATUS_LABELS[s];
          const n = stats.by_status?.[s] || 0;
          return (
            <Card key={s} className={`cursor-pointer ${statusFilter === s ? "ring-2 ring-primary" : ""}`}
                  onClick={() => setStatusFilter(s)}>
              <CardContent className="p-4">
                <div className="text-xs text-muted-foreground">{info.label}</div>
                <div className={`text-2xl font-bold text-${info.color}-600`}>{n}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="text-xs text-muted-foreground">
        Ostatnie 7 dni: <strong>{stats.last_7d_total}</strong> draftow wygenerowanych.
      </div>

      <Tabs value={statusFilter} onValueChange={setStatusFilter}>
        <TabsList>
          {Object.entries(STATUS_LABELS).map(([s, info]) => (
            <TabsTrigger key={s} value={s}>{info.label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {statusFilter === "pending_review" && items.length > 0 && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded">
          <Button variant="ghost" size="sm" onClick={selectedIds.size === items.length ? clearSelection : selectAll}>
            {selectedIds.size === items.length ? <CheckSquare className="w-4 h-4 mr-1" /> : <Square className="w-4 h-4 mr-1" />}
            {selectedIds.size === items.length ? "Odznacz" : "Zaznacz"} wszystkie
          </Button>
          <div className="text-sm">
            Zaznaczono: <strong>{selectedIds.size}</strong> z {items.length}
          </div>
          <div className="ml-auto flex gap-2">
            <Button variant="destructive" size="sm" onClick={() => bulkAction("reject")}
                    disabled={selectedIds.size === 0 || bulkLoading}>
              <Trash2 className="w-3 h-3 mr-1" /> Odrzuc zaznaczone
            </Button>
            <Button size="sm" onClick={() => bulkAction("send")}
                    disabled={selectedIds.size === 0 || bulkLoading}>
              {bulkLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
              Wyslij zaznaczone
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-muted-foreground">
          Brak draftow w statusie <strong>{STATUS_LABELS[statusFilter]?.label}</strong>.
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((d) => {
            const cls = d.classification || {};
            const langBadge = cls.language ? cls.language.toUpperCase() : "";
            const prioColor = cls.priority === "high" ? "red" : cls.priority === "medium" ? "amber" : "gray";
            return (
              <Card key={d.id} className={`cursor-pointer hover:border-primary transition ${selectedIds.has(d.id) ? "ring-2 ring-amber-400" : ""}`}
                    onClick={() => openDraft(d)}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    {statusFilter === "pending_review" && (
                      <button className="flex-shrink-0 mt-0.5" onClick={(e) => { e.stopPropagation(); toggleSelect(d.id); }}>
                        {selectedIds.has(d.id) ? <CheckSquare className="w-4 h-4 text-amber-600" /> : <Square className="w-4 h-4 text-muted-foreground" />}
                      </button>
                    )}
                    <div className="text-sm font-medium truncate flex-1" title={d.to}>
                      {d.to}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      {langBadge && <Badge variant="outline" className="text-[10px]">{langBadge}</Badge>}
                      <Badge variant="outline" className={`text-[10px] text-${prioColor}-600 border-${prioColor}-300`}>
                        {cls.priority || "?"}
                      </Badge>
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground truncate">
                    <Mail className="w-3 h-3 inline mr-1" />
                    {d.subject_original}
                  </div>
                  <div className="text-xs text-muted-foreground line-clamp-3 pt-1 border-t">
                    {(d.draft_reply || "").slice(0, 200)}...
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    {new Date(d.created_at).toLocaleString("pl-PL")}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
             onClick={closeDraft}>
          <div className="bg-background rounded-lg shadow-xl max-w-none w-full max-h-[90vh] overflow-y-auto"
               onClick={(e) => e.stopPropagation()}>
            <div className="p-6 space-y-4">
              <div className="flex items-start justify-between">
                <h2 className="text-lg font-bold">Draft odpowiedzi AI</h2>
                <Button variant="ghost" size="sm" onClick={closeDraft}><X className="w-4 h-4" /></Button>
              </div>

              <div className="text-sm space-y-1 bg-muted/50 p-3 rounded">
                <div><strong>Od:</strong> {selected.to}</div>
                <div><strong>Temat oryg.:</strong> {selected.subject_original}</div>
                <div className="text-xs text-muted-foreground mt-2 max-h-32 overflow-y-auto whitespace-pre-wrap">
                  {selected.body_original}
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold">Temat odpowiedzi</label>
                <Input value={editSubject} onChange={(e) => setEditSubject(e.target.value)} />
              </div>

              <div>
                <label className="text-xs font-semibold">Tresc odpowiedzi (mozesz edytowac)</label>
                <Textarea value={editBody} onChange={(e) => setEditBody(e.target.value)}
                          rows={16} className="font-mono text-sm" />
              </div>

              <div className="flex justify-between items-center pt-2 border-t">
                <div className="text-xs text-muted-foreground">
                  Klasyfikacja: <strong>{selected.classification?.category}</strong>
                  {" | "}Priorytet: <strong>{selected.classification?.priority}</strong>
                  {" | "}Jezyk: <strong>{selected.classification?.language}</strong>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={saveEdit} disabled={saving || sending}>
                    {saving && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                    Zapisz
                  </Button>
                  <Button variant="destructive" size="sm" onClick={reject} disabled={sending}>
                    Odrzuc
                  </Button>
                  <Button size="sm" onClick={send} disabled={sending || selected.status === "sent"}>
                    {sending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                    Wyslij
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
